/**
 * MembersTable — single generic table powering both Team and Users views.
 *
 * Driven by a column config array (see ../columns.tsx). Renders only the
 * columns whose ids are in `visibleColumnIds`. Headers are clickable to sort:
 *   - Click       → cycles asc → desc → off, replacing existing sort
 *   - Shift-click → adds/cycles secondary/tertiary sort, with priority badges
 *
 * topBand renders inside the card chrome above the column-header row (used
 * by TeamManagementView to embed the filter band).
 *
 * Rows are plain and non-interactive unless the caller passes `onRowPress`
 * (gated on the roster-edit permission by the caller) — used to open the
 * Employee Profile modal.
 */
import React from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, HStack, Pressable, ScrollView, VStack } from 'native-base';
import { Text } from '@pimm/base';
import type { ColumnContext, ColumnDef, SortKey } from './team-management-columns';
import { cycleSort } from './team-management-columns';
import type { TeamMember } from '../types';
import { MutedText, RowText } from './team-management-table-shared';

type Props = {
  members: TeamMember[];
  columns: ColumnDef[];
  visibleColumnIds: string[];
  sortKeys: SortKey[];
  onSortChange: (next: SortKey[]) => void;
  columnContext: ColumnContext;
  resolveLabel: (key: string) => string;
  minWidth: number;
  emptyMessage: string;
  topBand?: React.ReactNode;
  /**
   * Called with the clicked member's row when a row is pressed. Omit (or pass
   * undefined) to keep rows non-interactive — the caller is responsible for
   * gating this on the roster-edit permission.
   */
  onRowPress?: (member: TeamMember) => void;
  /**
   * When true, columns marked `confidential: true` in their ColumnDef render a
   * masked placeholder (••••) instead of the real value. Has no effect on the
   * Users tab (those ColumnDefs carry confidential: false).
   */
  maskConfidential?: boolean;
};

export const MembersTable: React.FC<Props> = ({
  members,
  columns,
  visibleColumnIds,
  sortKeys,
  onSortChange,
  columnContext,
  resolveLabel,
  minWidth,
  emptyMessage,
  topBand,
  onRowPress,
  maskConfidential = false,
}) => {
  // Resolve which columns to render in which order. Order comes from the
  // column-def array, not from visibleColumnIds (so users can't reorder via
  // the show/hide popover — that's a separate v2 feature).
  const visibleColumns = columns.filter(c => visibleColumnIds.includes(c.id) || c.alwaysVisible);

  const handleHeaderPress = (col: ColumnDef, shiftHeld: boolean) => {
    if (!col.sortFn) return;
    onSortChange(cycleSort(sortKeys, col.id, shiftHeld));
  };

  return (
    <Box bg="white" rounded="lg" borderWidth={1} borderColor="gray.200" overflow="hidden" flex={1}>
      {topBand}
      {/* contentContainerStyle flexGrow:1 lets the table stretch to fill the
          card when the viewport is wider than minWidth (so the vertical
          scrollbar rides the card's right edge instead of floating at the
          columns' edge). When the viewport is narrower than minWidth, the
          content keeps its floor width and scrolls horizontally as normal. */}
      <ScrollView horizontal flex={1} showsHorizontalScrollIndicator contentContainerStyle={{ flexGrow: 1 }}>
        <VStack minW={`${minWidth}px`} flex={1}>
          {/* Header row */}
          <HStack bg="coolGray.50" borderBottomWidth={1} borderColor="gray.200" alignItems="center">
            {visibleColumns.map(col => {
              const sortIdx = sortKeys.findIndex(s => s.id === col.id);
              const sk = sortIdx >= 0 ? sortKeys[sortIdx] : null;
              const isSortable = !!col.sortFn;
              // Sizing props live on the OUTER wrapper (Pressable or Box) so
              // header widths match the body row's direct-Box widths. The
              // inner content is just padding + the label + sort indicator.
              const sizingProps = {
                flex: col.width == null ? col.flex ?? 1 : undefined,
                w: col.width,
              };
              const inner = (
                // `w="100%"` keeps the header HStack stretched to the full
                // cell width so the px={3} padding sits at the SAME left
                // offset as the body cell's px={3}. Without this, the inner
                // HStack content-sizes inside the outer Pressable and the
                // header label floats relative to the body cell text.
                <HStack w="100%" space={1.5} alignItems="center" justifyContent="flex-start" px={3} py={2}>
                  <Text size="sm" fontWeight={600} color="gray.700">
                    {resolveLabel(col.labelKey)}
                  </Text>
                  {isSortable && sk && (
                    <HStack space={0.5} alignItems="center">
                      {sk.dir === 'asc' ? <ChevronUpIcon size={3} color="primary.700" /> : <ChevronDownIcon size={3} color="primary.700" />}
                      {sortKeys.length > 1 && (
                        <Box bg="primary.500" rounded="full" w={3.5} h={3.5} alignItems="center" justifyContent="center">
                          <Text size="2xs" fontWeight={700} color="white">
                            {sortIdx + 1}
                          </Text>
                        </Box>
                      )}
                    </HStack>
                  )}
                </HStack>
              );
              return isSortable ? (
                <Pressable
                  key={col.id}
                  {...sizingProps}
                  onPress={(e: any) => {
                    const shift = !!(e?.shiftKey || e?.nativeEvent?.shiftKey);
                    handleHeaderPress(col, shift);
                  }}
                  _hover={{ bg: 'gray.100' }}
                >
                  {inner}
                </Pressable>
              ) : (
                <Box key={col.id} {...sizingProps}>
                  {inner}
                </Box>
              );
            })}
          </HStack>

          {/* Rows */}
          <ScrollView flex={1} showsVerticalScrollIndicator>
            {members.map((m, idx) => {
              const isLast = idx === members.length - 1;
              const row = (
                <HStack alignItems="center" borderBottomWidth={isLast ? 0 : 1} borderColor="gray.100" bg="white">
                  {visibleColumns.map(col => (
                    <Box
                      key={col.id}
                      flex={col.width == null ? col.flex ?? 1 : undefined}
                      w={col.width}
                      px={3}
                      py={3}
                      justifyContent="center"
                      alignItems="flex-start"
                    >
                      {maskConfidential && col.confidential ? <MutedText>••••</MutedText> : col.renderCell(m, columnContext)}
                    </Box>
                  ))}
                </HStack>
              );
              return onRowPress ? (
                <Pressable key={m.id} onPress={() => onRowPress(m)} _hover={{ bg: 'gray.50' }}>
                  {row}
                </Pressable>
              ) : (
                <Box key={m.id}>{row}</Box>
              );
            })}
            {members.length === 0 && (
              <Box px={4} py={8} alignItems="center">
                <RowText>{emptyMessage}</RowText>
              </Box>
            )}
          </ScrollView>
        </VStack>
      </ScrollView>
    </Box>
  );
};
